import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { FormEventHandler, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FieldErrorsImpl,
  FieldValues,
  Path,
  UnPackAsyncDefaultValues,
  UseFormRegister,
  UseFormSetValue,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  GetApprovalApplicationAttachedFileQueryResult,
  useGetApprovalApplicationAttachedFile,
  useGetApprovalApplicationDetail,
  useGetApprovalUsers,
  usePostApprovalApplicationAdd,
  usePostApprovalApplicationDelete,
  usePostApprovalApplicationUpdate,
} from '../../../../api/approval-applications/approval-applications';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import {
  ApprovalApplicationAdd,
  ApprovalApplicationDetail,
  ApprovalApplicationUpdate,
  AttachedFile,
  ProductionMasters,
} from '../../../../api/model';
import { useGetProductionDetail } from '../../../../api/productions/productions';
import POST_MESSAGES from '../../../../constants/postMessages';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import usePostMessage from '../../../../hooks/usePostMessage';
import yup from '../../../../utils/custom-yup';
import ErrorComponent from '../../../atoms/ErrorComponent';
import FileDrop, { FileWithId } from '../../../atoms/FileDrop';
import SingleSelect, { transformSingleSelectValue } from '../../../atoms/SingleSelect';
import WaitComponent from '../../../atoms/WaitComponent';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import AttachedFileDownloader from './AttachedFileDownloader';
import { SingleSelectGroup } from './FormElements';
import { ApprovalApplicationStatuses, ApprovalApplicationTypes } from './ProductionApprovalApplicationsTab';

// 操作(1:保存 2:申請 3:承認 4:否認)
const ApprovalApplicationActions = {
  save: 1,
  application: 2,
  approve: 3,
  reject: 4,
} as { [name: string]: number };

// Gate(1:制作前報告, 2:キックオフ会, 3:G1, 4:G2, 5:G3, 6:完了, 7:再編集, 8:削除)
const ApprovalApplicationGates = {
  reEditiong: 7,
  deletion: 8,
};

interface ProductionApprovalApplicationAddProps {
  productionId: number;
  approvalApplicationTypeId: number;
  masters: ProductionMasters;
}

/**
 * 承認タブの承認追加画面
 * @param param0
 * @returns
 */
function ProductionApprovalApplicationAdd({
  productionId,
  approvalApplicationTypeId,
  masters,
}: ProductionApprovalApplicationAddProps) {
  return (
    <ProductionApprovalApplicationMain
      productionId={productionId}
      approvalApplicationTypeId={approvalApplicationTypeId}
      approvalApplicationId={undefined}
      approvalApplicationDetail={undefined}
      isReadonly={false}
      masters={masters}
    />
  );
}

interface ProductionApprovalApplicationEditProps {
  productionId: number;
  approvalApplicationId: number;
  isReadonly: boolean;
  masters: ProductionMasters;
}

/**
 * 承認タブの承認編集画面
 * @param param0
 * @returns
 */
function ProductionApprovalApplicationEdit({
  productionId,
  approvalApplicationId,
  isReadonly,
  masters,
}: ProductionApprovalApplicationEditProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const {
    data: approvalApplicationDetailResult,
    isError,
    error,
  } = useGetApprovalApplicationDetail(approvalApplicationId, options);

  if (isError) {
    return <ErrorComponent error={error} />;
  }

  const approvalApplicationDetail = approvalApplicationDetailResult?.data;
  return approvalApplicationDetail != null ? (
    <ProductionApprovalApplicationMain
      productionId={productionId}
      approvalApplicationTypeId={undefined}
      approvalApplicationId={approvalApplicationId}
      approvalApplicationDetail={approvalApplicationDetail}
      isReadonly={
        isReadonly || approvalApplicationDetail.approval_application_status_id === ApprovalApplicationStatuses.approved
      }
      masters={masters}
    />
  ) : (
    <WaitComponent />
  );
}

export { ProductionApprovalApplicationAdd, ProductionApprovalApplicationEdit };

interface ProductionApprovalApplicationMainProps {
  productionId: number;
  approvalApplicationTypeId: number | undefined;
  approvalApplicationId: number | undefined;
  approvalApplicationDetail: ApprovalApplicationDetail | undefined;
  isReadonly: boolean;
  masters: ProductionMasters;
}

/**
 * 承認タブの承認編集画面メインエリアコンポーネント
 * @param param0
 * @returns
 */
function ProductionApprovalApplicationMain({
  productionId,
  approvalApplicationTypeId,
  approvalApplicationId,
  approvalApplicationDetail,
  isReadonly,
  masters,
}: ProductionApprovalApplicationMainProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const selectedActionRef = useRef<number>();

  const schema = yup.object({
    approval_application_gate_id: yup.number().nullable().transform(transformSingleSelectValue),
    approval1_user_id: yup.lazy(() =>
      selectedActionRef.current === ApprovalApplicationActions.application
        ? yup.number().required().transform(transformSingleSelectValue)
        : yup.number().nullable().transform(transformSingleSelectValue)
    ),
    approval2_user_id: yup.number().nullable().transform(transformSingleSelectValue),
    content: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
  });

  const lastSavedMessage = useMemo(
    () =>
      approvalApplicationDetail?.approval_application_messages.find(
        (x) => x.action === ApprovalApplicationActions.save
      ),
    [approvalApplicationDetail?.approval_application_messages]
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      ...approvalApplicationDetail,
      content: lastSavedMessage?.content ?? '',
      applicationFiles: [] as FileWithId[],
    },
    resolver: yupResolver(schema),
  });

  // 更新処理定義
  const options = useDefaultOptions(language);

  const { mutateAsync: approvalApplicationUpdateAsync, error: approvalApplicationUpdateError } =
    usePostApprovalApplicationUpdate(options);

  const [displayAapprovalApplicationUpdateError, setDisplayApprovalApplicationUpdateError] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setDisplayApprovalApplicationUpdateError(approvalApplicationUpdateError != null);
    setIsSubmitting(false);
  }, [approvalApplicationUpdateError]);

  // 追加処理定義
  const { mutateAsync: approvalApplicationAddAsync, error: approvalApplicationAddError } =
    usePostApprovalApplicationAdd(options);

  const [displayApprovalApplicationAddError, setDisplayApprovalApplicationAddError] = useState(false);

  useEffect(() => {
    setDisplayApprovalApplicationAddError(approvalApplicationAddError != null);
    setIsSubmitting(false);
  }, [approvalApplicationAddError]);

  // 削除処理定義
  const { mutateAsync: approvalApplicationDeleteAsync, error: approvalApplicationDeleteError } =
    usePostApprovalApplicationDelete(options);

  const [displayApprovalApplicationDeleteError, setDisplayApprovalApplicationDeleteError] = useState(false);

  useEffect(() => {
    setDisplayApprovalApplicationDeleteError(approvalApplicationDeleteError != null);
    setIsSubmitting(false);
  }, [approvalApplicationDeleteError]);

  // 資料情報取得
  const productionDetailResponse = useGetProductionDetail(productionId, options);
  const productionDetail = productionDetailResponse.data?.data;

  // ゲート項目のフィルタ
  const gates = useMemo(() => {
    const disabledGates = [
      { divisionId: 0, disabledGateIds: [7, 8] }, // その他
      { divisionId: 1, disabledGateIds: [7, 8] }, // OM
      { divisionId: 2, disabledGateIds: [7, 8] }, // OM（追補）
      { divisionId: 4, disabledGateIds: [5, 7, 8] }, // dOM
      { divisionId: 5, disabledGateIds: [4, 5, 7, 8] }, // Web
      { divisionId: 13, disabledGateIds: [7, 8] }, // OG
    ];

    let disabledGate = disabledGates.find(
      (x) => x.divisionId === productionDetail?.production_basic_info?.production_division_id
    );
    if (disabledGate == null) {
      [disabledGate] = disabledGates;
    }

    switch (approvalApplicationTypeId ?? approvalApplicationDetail?.approval_application_type_id) {
      case ApprovalApplicationTypes.approval:
        return (
          masters.approval_application_gates?.filter(
            (x) => !disabledGate?.disabledGateIds.includes(x.approval_application_gate_id ?? 0)
          ) ?? []
        );
      case ApprovalApplicationTypes.deletion:
        return (
          masters.approval_application_gates?.filter(
            (x) => x.approval_application_gate_id === ApprovalApplicationGates.deletion
          ) ?? []
        );
      case ApprovalApplicationTypes.reEditing:
        return (
          masters.approval_application_gates?.filter(
            (x) => x.approval_application_gate_id === ApprovalApplicationGates.reEditiong
          ) ?? []
        );
      default:
        return [];
    }
  }, [
    masters.approval_application_gates,
    productionDetail?.production_basic_info?.production_division_id,
    approvalApplicationTypeId,
    approvalApplicationDetail?.approval_application_type_id,
  ]);

  // 追加，更新処理実行
  const { postMessage } = usePostMessage(POST_MESSAGES.ReloadApprovalApplicationList);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      const upsertApprovalApplication = async (approvalApplicationAction: number) => {
        await handleSubmit(async (data) => {
          if (approvalApplicationId) {
            await approvalApplicationUpdateAsync({
              approvalApplicationId,
              data: {
                ...data,
                action: approvalApplicationAction,
                'files[]': data.applicationFiles.filter((x) => !x.id),
                file_ids: data.applicationFiles
                  .filter((x) => x.id != null)
                  .map((x) => x.id)
                  .join(','),
              } as ApprovalApplicationUpdate,
            });
          } else {
            await approvalApplicationAddAsync({
              productionId,
              data: {
                ...data,
                action: approvalApplicationAction,
                approval_application_type_id: approvalApplicationTypeId,
                'files[]': data.applicationFiles.filter((x) => !x.id),
                file_ids: data.applicationFiles.filter((x) => x.id != null).join(','),
              } as ApprovalApplicationAdd,
            });
          }

          setIsSubmitting(false);
          postMessage();
          window.close();
        })();
      };

      e.preventDefault();
      setIsSubmitting(true);

      const submitterId: string = e.nativeEvent instanceof SubmitEvent ? e.nativeEvent.submitter?.id ?? '' : '';

      if (submitterId in ApprovalApplicationActions) {
        selectedActionRef.current = ApprovalApplicationActions[submitterId];
        await upsertApprovalApplication(ApprovalApplicationActions[submitterId]);
      }

      setIsSubmitting(false);
    },
    [
      setIsSubmitting,
      handleSubmit,
      productionId,
      approvalApplicationTypeId,
      approvalApplicationId,
      postMessage,
      approvalApplicationUpdateAsync,
      approvalApplicationAddAsync,
    ]
  );

  // 削除実行
  const [displayDeleteConfirmation, setDisplayDeleteConfirmation] = useState(false);

  const onDeleteClicked = useCallback(async () => {
    setDisplayDeleteConfirmation(false);

    if (approvalApplicationId != null) {
      setIsSubmitting(true);
      await approvalApplicationDeleteAsync({ approvalApplicationId });
      setIsSubmitting(false);
    }

    postMessage();
    window.close();
  }, [approvalApplicationId, approvalApplicationDeleteAsync, postMessage]);

  // キャンセル
  const onCancelClicked: MouseEventHandler<HTMLInputElement> = useCallback((e) => {
    e.preventDefault();
    window.close();
  }, []);

  // 遷移状態フラグ生成
  const loginUserInfoResponse = useGetLoginUserInfo(options);
  const loginUser = loginUserInfoResponse.data?.data;

  const { isAdd, isNewOrRejected, isForApprovalUser } = useMemo(
    () => ({
      isAdd: !approvalApplicationId,
      isNewOrRejected:
        (approvalApplicationDetail?.approval_application_status_id === ApprovalApplicationStatuses.new ||
          approvalApplicationDetail?.approval_application_status_id === ApprovalApplicationStatuses.rejected) &&
        approvalApplicationDetail.application_user_id === loginUser?.user_id,
      isForApprovalUser:
        approvalApplicationDetail?.approval_application_status_id === ApprovalApplicationStatuses.requested &&
        ((!approvalApplicationDetail.approval1_at &&
          approvalApplicationDetail.approval1_user_id === loginUser?.user_id) ||
          (approvalApplicationDetail?.approval1_at != null &&
            !approvalApplicationDetail?.approval2_at &&
            approvalApplicationDetail.approval2_user_id === loginUser?.user_id)),
    }),
    [
      approvalApplicationId,
      loginUser?.user_id,
      approvalApplicationDetail?.approval_application_status_id,
      approvalApplicationDetail?.application_user_id,
      approvalApplicationDetail?.approval1_at,
      approvalApplicationDetail?.approval2_at,
      approvalApplicationDetail?.approval1_user_id,
      approvalApplicationDetail?.approval2_user_id,
    ]
  );

  const watchGateId = watch('approval_application_gate_id');

  // 添付ファイル削除(確認依頼と同じコンポーネントを使用しているため)
  const refetchDetail = () => 1; // 空関数

  return (
    <>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting || isReadonly} className="fieldset-reset">
          <div className="approval">
            <div className="approval1">
              <ul>
                <li>
                  <label>{t('資料No.')}</label>
                  <span>
                    {approvalApplicationDetail?.production_no ?? productionDetail?.production_basic_info?.production_no}
                  </span>
                </li>
                {isAdd || isNewOrRejected ? (
                  <SelectApprovalUsers
                    productionId={productionId}
                    gateId={watchGateId ?? approvalApplicationDetail?.approval_application_gate_id}
                    order={1}
                    name="approval1_user_id"
                    register={register}
                    label={t('承認者1')}
                    errors={errors}
                  />
                ) : (
                  <li>
                    <label>{t('承認者1')}</label>
                    <span>{approvalApplicationDetail?.approval1_user_name}</span>
                  </li>
                )}
                {isAdd || isNewOrRejected ? (
                  <SelectApprovalUsers
                    productionId={productionId}
                    gateId={watchGateId ?? approvalApplicationDetail?.approval_application_gate_id}
                    order={2}
                    name="approval2_user_id"
                    register={register}
                    label={t('承認者2')}
                    errors={errors}
                  />
                ) : (
                  <li>
                    <label>{t('承認者2')}</label>
                    <span>{approvalApplicationDetail?.approval2_user_name}</span>
                  </li>
                )}
              </ul>
            </div>
            <div className="approval2">
              <ul>
                <li>
                  <label htmlFor="">{t('資料区分')}</label>
                  <span>
                    {
                      masters.production_divisions?.find(
                        (x) =>
                          x.production_division_id ===
                          (approvalApplicationDetail?.production_division_id ??
                            productionDetail?.production_basic_info?.production_division_id)
                      )?.production_division_name
                    }
                  </span>
                </li>
                <li>
                  <label htmlFor="">{t('ゲート')}</label>
                  {isAdd || isNewOrRejected ? (
                    <span>
                      <SingleSelect
                        id="approval_application_gate_id"
                        name="approval_application_gate_id"
                        allowEmpty
                        options={gates}
                        register={register}
                        getValue={(x) => x.approval_application_gate_id as number}
                        getLabel={(x) => x.approval_application_gate_name as string}
                      />
                    </span>
                  ) : (
                    <span>
                      {approvalApplicationDetail?.approval_application_gate_id != null
                        ? masters.approval_application_gates?.find(
                            (x) =>
                              x.approval_application_gate_id === approvalApplicationDetail?.approval_application_gate_id
                          )?.approval_application_gate_name
                        : ''}
                    </span>
                  )}
                </li>
                <li>
                  <label htmlFor="">{t('ステータス')}</label>
                  <span>
                    {isAdd
                      ? t('新規作成')
                      : masters.approval_application_statuses?.find(
                          (x) =>
                            x.approval_application_status_id ===
                            approvalApplicationDetail?.approval_application_status_id
                        )?.approval_application_status_name}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="threadfr mt30">
            <label htmlFor="">{t('スレッド')}</label>
            <div>
              <ul>
                {approvalApplicationDetail?.approval_application_messages
                  .filter((x) => x.action !== ApprovalApplicationActions.save)
                  .map((message) => (
                    <li key={message.approval_application_message_id}>
                      <p className="t-user">{message.send_user_name}</p>
                      <p className="t-comment">
                        {message.content}{' '}
                        <span className="checkmark">
                          <i className="fa-solid fa-circle-check" />
                          {
                            masters.approval_application_statuses?.find(
                              (x) => x.approval_application_status_id === message.action
                            )?.approval_application_status_name
                          }
                        </span>
                      </p>
                      <span className="drawingfile">
                        {message.attached_files?.map((attachedFile) => (
                          <ApprovalApplicationAttachedFile
                            attachedFile={attachedFile}
                            key={attachedFile.file_id}
                            refetchDetail={refetchDetail}
                          />
                        ))}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          {(isAdd || isNewOrRejected || isForApprovalUser) && (
            <>
              {' '}
              <div className="formarea1 mt20">
                <label htmlFor="">{t('内容')}</label>
                <textarea id="content" rows={10} cols={33} {...register('content')} />
              </div>
              <div className="formarea1 mt10">
                <label htmlFor="">{t('添付ファイル')}</label>
                <div>
                  <FileDrop
                    id="applicationFiles"
                    name="applicationFiles"
                    setValue={setValue as unknown as UseFormSetValue<FieldValues>}
                    selectedFiles={lastSavedMessage?.attached_files?.map(
                      (x) => ({ id: x.file_id, name: x.file_name } as FileWithId)
                    )}
                  />
                </div>
              </div>
            </>
          )}
        </fieldset>

        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="btnfr btnfr3 mb0">
            <div>
              {!isReadonly && isNewOrRejected && (
                <span>
                  <input
                    type="submit"
                    id="delete"
                    name="delete"
                    value={t('削除').toString()}
                    className="btn1 btnbg-g link-b8"
                    onClick={(e) => {
                      e.preventDefault();
                      setDisplayDeleteConfirmation(true);
                    }}
                  />
                </span>
              )}
              <span>
                <input
                  type="submit"
                  id="cancel"
                  name="cancel"
                  value={t('キャンセル').toString()}
                  className="btn1 btnbg-g link-b8"
                  onClick={(e) => onCancelClicked(e)}
                />
              </span>
              {!isReadonly && (isAdd || isNewOrRejected || isForApprovalUser) && (
                <span>
                  <input type="submit" id="save" name="save" value={t('保存').toString()} className="btn1 lsp1" />
                </span>
              )}
              {!isReadonly && (isAdd || isNewOrRejected) && (
                <span>
                  <input
                    type="submit"
                    id="application"
                    name="application"
                    value={t('申請').toString()}
                    className="btn1 lsp1 detailbtn1-8b"
                  />
                </span>
              )}
              {!isReadonly && isForApprovalUser && (
                <span>
                  <input type="submit" id="reject" name="reject" value={t('差戻し').toString()} className="btn1" />
                </span>
              )}
              {!isReadonly && isForApprovalUser && (
                <span>
                  <input type="submit" id="approve" name="approve" value={t('承認').toString()} className="btn1" />
                </span>
              )}
            </div>
          </div>
        </fieldset>
      </form>
      {isSubmitting && <SpinnerModal />}
      {displayAapprovalApplicationUpdateError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayApprovalApplicationUpdateError(false);
          }}
          error={approvalApplicationUpdateError}
        />
      )}
      {displayApprovalApplicationAddError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayApprovalApplicationAddError(false);
          }}
          error={approvalApplicationAddError}
        />
      )}
      {displayApprovalApplicationDeleteError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayApprovalApplicationDeleteError(false);
          }}
          error={approvalApplicationDeleteError}
        />
      )}
      {displayDeleteConfirmation && (
        <ConfirmModal onClickOk={() => onDeleteClicked()} onClickCancel={() => setDisplayDeleteConfirmation(false)}>
          <div>{t('削除しますか')}</div>
        </ConfirmModal>
      )}
    </>
  );
}

interface ApprovalApplicationAttachedFileProps {
  attachedFile: AttachedFile;
  refetchDetail: () => void;
}

/**
 * 添付ファイル表示，ダウンロード用コンポーネント
 * @param param0
 * @returns
 */
function ApprovalApplicationAttachedFile({ attachedFile, refetchDetail }: ApprovalApplicationAttachedFileProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const options = useOptionsForBinary(language);

  const { refetch: refetchApprovalApplicationAttachedFile, error: getApprovalApplicationAttachedFileError } =
    useGetApprovalApplicationAttachedFile<GetApprovalApplicationAttachedFileQueryResult, AxiosError<Blob>>(
      attachedFile.file_id,
      options
    );

  return (
    <AttachedFileDownloader
      attachedFile={attachedFile}
      refetch={refetchApprovalApplicationAttachedFile}
      error={getApprovalApplicationAttachedFileError}
      mode="Application"
      refetchDetail={refetchDetail}
    />
  );
}

interface SelectApprovalUsersProps<S extends FieldValues> {
  productionId: number;
  gateId: number | undefined;
  order: number;
  label: DefaultTFuncReturn;
  name: Path<UnPackAsyncDefaultValues<S>>;
  errors: Partial<
    FieldErrorsImpl<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [x: string]: any;
    }>
  >;
  register: UseFormRegister<S>;
}

/**
 * 承認者一覧ドロップダウン
 * @param param0
 * @returns
 */
function SelectApprovalUsers<S extends FieldValues>({
  productionId,
  gateId,
  order,
  label,
  name,
  errors,
  register,
}: SelectApprovalUsersProps<S>) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 承認者一覧取得
  const options = useDefaultOptions(language);

  const getApprovalUsersResponse = useGetApprovalUsers(productionId, { approval_application_gate_id: gateId }, options);

  const approvalUsers = useMemo(() => {
    if (order === 1) {
      return getApprovalUsersResponse.data?.data.approval1Users;
    }
    if (order === 2) {
      return getApprovalUsersResponse.data?.data.approval2Users;
    }
    return [];
  }, [getApprovalUsersResponse.data?.data.approval1Users, getApprovalUsersResponse.data?.data.approval2Users, order]);

  return approvalUsers != null ? (
    <SingleSelectGroup
      name={name}
      allowEmpty
      options={approvalUsers ?? []}
      register={register}
      getValue={(x) => x.user_id}
      getLabel={(x) => x.user_name}
      label={label}
      errors={errors}
    />
  ) : (
    <li>
      <label htmlFor={name}>{label}</label>
      <span>
        <span className="dropdown">
          <select disabled>
            <option>{t('お待ちください')}</option>
          </select>
        </span>
      </span>
    </li>
  );
}
